import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  CircularProgress,
  TextField,
  Chip,
  Divider,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { cancelIngenicoOpenTransaction } from '../hooks/fiscalDevices/ingenico';

/**
 * QuickSaleMiscDialogs
 *
 * QuickSaleScreen'den taşınan bağımsız inline onay/not/PIN diyalogları:
 *  - Ingenico açık-fiş kurtarma
 *  - Sepeti temizle onayı
 *  - Müşteri notu
 *  - Paket modu ödeme yöntemi seçimi
 *  - Satın alınan ürünler / iade listesi
 *  - İade onayı
 *  - Ürün kartı sıralama PIN
 *
 * Davranış birebir korunur: container koşulsuz render edilir, her diyaloğun
 * orijinal guard'ı (open={...}) bu component içinde aynen yaşar.
 * Props-bag deseni RestaurantFlowModals.tsx ile uyumludur.
 */
interface QuickSaleMiscDialogsProps {
  // Ingenico açık-fiş kurtarma
  isIngenicoRecoveryModalOpen: boolean;
  ingenicoRecoveryData: any;
  isIngenicoRecoveryProcessing: boolean;
  handleIngenicoRecoveryCancel: any;
  handleIngenicoRecoveryContinue: any;
  setIsIngenicoRecoveryProcessing: any;
  setIsIngenicoRecoveryModalOpen: any;
  setIngenicoRecoveryData: any;
  showAlertModal: any;

  // Sepeti temizle onayı
  clearCartConfirmOpen: boolean;
  handleCloseClearCartConfirm: any;
  handleConfirmClearCart: any;

  // Müşteri notu
  isCustomerNoteModalOpen: boolean;
  setIsCustomerNoteModalOpen: any;
  customerNote: string;
  setCustomerNote: any;

  // Paket ödeme yöntemi
  packagePaymentModalOpen: boolean;
  setPackagePaymentModalOpen: any;
  setPendingPackageOrderData: any;
  addOnOrderId: any;
  pendingPackageOrderData: any;
  handlePackagePaymentSelect: any;
  otherPaymentOptions: any[];

  // Satın alınanlar / iade
  isPurchaseItemsOpen: boolean;
  setIsPurchaseItemsOpen: any;
  selectedRefundItem: any;
  setSelectedRefundItem: any;
  refundLoading: boolean;
  refundConfirmOpen: boolean;
  setRefundConfirmOpen: any;
  prepaidCard: any;
  purchaseItems: any[];
  purchaseItemsLoading: boolean;
  purchaseItemsGrandTotal: number;
  handleRefundItem: any;
  formatCurrency: (amount: number) => string;

  // Ürün sıralama PIN
  isProductSortPinDialogOpen: boolean;
  setIsProductSortPinDialogOpen: any;
  productSortPinInput: string;
  setProductSortPinInput: any;
  productSortPinError: boolean;
  setProductSortPinError: any;
  handleProductSortPinDigit: any;
  handleProductSortPinBackspace: any;
}

const QuickSaleMiscDialogs: React.FC<QuickSaleMiscDialogsProps> = ({
  isIngenicoRecoveryModalOpen,
  ingenicoRecoveryData,
  isIngenicoRecoveryProcessing,
  handleIngenicoRecoveryCancel,
  handleIngenicoRecoveryContinue,
  setIsIngenicoRecoveryProcessing,
  setIsIngenicoRecoveryModalOpen,
  setIngenicoRecoveryData,
  showAlertModal,
  clearCartConfirmOpen,
  handleCloseClearCartConfirm,
  handleConfirmClearCart,
  isCustomerNoteModalOpen,
  setIsCustomerNoteModalOpen,
  customerNote,
  setCustomerNote,
  packagePaymentModalOpen,
  setPackagePaymentModalOpen,
  setPendingPackageOrderData,
  addOnOrderId,
  pendingPackageOrderData,
  handlePackagePaymentSelect,
  otherPaymentOptions,
  isPurchaseItemsOpen,
  setIsPurchaseItemsOpen,
  selectedRefundItem,
  setSelectedRefundItem,
  refundLoading,
  refundConfirmOpen,
  setRefundConfirmOpen,
  prepaidCard,
  purchaseItems,
  purchaseItemsLoading,
  purchaseItemsGrandTotal,
  handleRefundItem,
  formatCurrency,
  isProductSortPinDialogOpen,
  setIsProductSortPinDialogOpen,
  productSortPinInput,
  setProductSortPinInput,
  productSortPinError,
  setProductSortPinError,
  handleProductSortPinDigit,
  handleProductSortPinBackspace,
}) => {
  const { t } = useTranslation('salesSystem');

  return (
    <>
      {/* Ingenico açık-fiş kurtarma */}
      <Dialog
        open={isIngenicoRecoveryModalOpen}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'warning.contrastText', fontWeight: 'bold' }}>
          Cihazda Açık Fiş Bulundu
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {ingenicoRecoveryData && (
            <Box>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Ingenico 5000F cihazında tamamlanmamış bir fiş tespit edildi. Bu fiş önceki bir satış işleminden kalmış olabilir.
              </Typography>
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 2 }}>
                {ingenicoRecoveryData.items.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'bold' }}>Ürünler:</Typography>
                    {ingenicoRecoveryData.items.map((item: any, idx: number) => (
                      <Typography key={idx} variant="body2" sx={{ pl: 1 }}>
                        {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name} - {item.amountTL.toFixed(2)} TL
                      </Typography>
                    ))}
                  </Box>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Fiş Toplamı:</strong> {ingenicoRecoveryData.totalAmountTL.toFixed(2)} TL
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Ödenen:</strong> {ingenicoRecoveryData.totalPaymentTL.toFixed(2)} TL
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: ingenicoRecoveryData.remainingAmountTL > 0 ? 'error.main' : 'success.main', fontWeight: 'bold' }}>
                  <strong>Kalan:</strong> {ingenicoRecoveryData.remainingAmountTL.toFixed(2)} TL
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Aşağıdaki seçeneklerden birini kullanarak devam edebilirsiniz:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Ödemeye Devam Et:</strong> Kalan tutarı ödeyerek fişi kapatır (parçalı ödeme ekranı açılır).
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Fişi İptal Et:</strong> Açık fişi tamamen iptal eder ve sıfırdan başlamanıza olanak sağlar.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleIngenicoRecoveryCancel}
            variant="outlined"
            color="error"
            disabled={isIngenicoRecoveryProcessing}
            sx={{ fontWeight: 'bold' }}
          >
            {isIngenicoRecoveryProcessing ? <CircularProgress size={20} /> : 'Fişi İptal Et'}
          </Button>
          {ingenicoRecoveryData && ingenicoRecoveryData.remainingAmountTL > 0 && !ingenicoRecoveryData.paymentsComplete && (
            <Button
              onClick={handleIngenicoRecoveryContinue}
              variant="contained"
              color="success"
              disabled={isIngenicoRecoveryProcessing}
              sx={{ fontWeight: 'bold' }}
            >
              Ödemeye Devam Et
            </Button>
          )}
          {ingenicoRecoveryData && ingenicoRecoveryData.paymentsComplete && (
            <Button
              onClick={async () => {
                setIsIngenicoRecoveryProcessing(true);
                try {
                  const result = await cancelIngenicoOpenTransaction();
                  if (result.success) {
                    showAlertModal('Fiş Kapatıldı', 'Ödemesi tamamlanmış fiş başarıyla kapatıldı.');
                  } else {
                    showAlertModal('Fiş Kapatılamadı', result.message || 'Fiş kapatılamadı. Cihazı kontrol edip tekrar deneyin.');
                  }
                } finally {
                  setIsIngenicoRecoveryProcessing(false);
                  setIsIngenicoRecoveryModalOpen(false);
                  setIngenicoRecoveryData(null);
                }
              }}
              variant="contained"
              color="primary"
              disabled={isIngenicoRecoveryProcessing}
              sx={{ fontWeight: 'bold' }}
            >
              {isIngenicoRecoveryProcessing ? <CircularProgress size={20} /> : 'Fişi Kapat'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Sepeti temizle onayı */}
      <Dialog
        open={clearCartConfirmOpen}
        onClose={handleCloseClearCartConfirm}
        aria-labelledby="clear-cart-confirm-title"
      >
        <DialogTitle id="clear-cart-confirm-title">
          Sepeti Temizle
        </DialogTitle>
        <DialogContent>
          <Typography>
            Sepetteki tüm ürünler kaldırılacak. Bu işlemi onaylıyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClearCartConfirm} color="inherit">
            Vazgeç
          </Button>
          <Button onClick={handleConfirmClearCart} color="error" variant="contained" autoFocus>
            Temizle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Müşteri Notu Modalı */}
      <Dialog
        open={isCustomerNoteModalOpen}
        onClose={() => setIsCustomerNoteModalOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntered: () => {
            // Modal tamamen açıldıktan sonra input'a focus yap
            const input = document.getElementById('customer-note-input');
            if (input) {
              input.focus();
            }
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <NoteAddIcon />
          {t('quickSaleScreen.customerNoteModal.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            id="customer-note-input"
            autoFocus
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            label={t('quickSaleScreen.customerNoteModal.noteLabel')}
            placeholder={t('quickSaleScreen.customerNoteModal.notePlaceholder')}
            value={customerNote}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCustomerNote(e.target.value)}
            inputProps={{ 
              maxLength: 200,
              inputMode: 'text', // Dokunmatik cihazlarda klavyeyi otomatik açar
            }}
            helperText={t('quickSaleScreen.customerNoteModal.noteHelper', { count: customerNote.length })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setCustomerNote('');
              setIsCustomerNoteModalOpen(false);
            }}
            color="error"
          >
            {t('quickSaleScreen.customerNoteModal.clearAndClose')}
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setIsCustomerNoteModalOpen(false)}
            color="primary"
          >
            {t('quickSaleScreen.customerNoteModal.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PAKET MODU: Ödeme Yöntemi Seçim Modalı */}
      <Dialog
        open={packagePaymentModalOpen}
        onClose={() => {
          setPackagePaymentModalOpen(false);
          setPendingPackageOrderData(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', bgcolor: addOnOrderId ? 'success.main' : 'primary.main', color: 'white' }}>
          {addOnOrderId ? '➕ İlave Sipariş Onayı' : '💳 Ödeme Yöntemi Seçin'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
            {addOnOrderId 
              ? `Eklenecek Ürünler: ${pendingPackageOrderData?.orderDto.Items?.length || 0} adet`
              : `Toplam: ₺${pendingPackageOrderData?.orderDto.TotalAmount?.toFixed(2) || '0.00'}`
            }
          </Typography>
          {addOnOrderId && (
            <Typography variant="body2" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
              Tutar: <strong>₺{pendingPackageOrderData?.orderDto.TotalAmount?.toFixed(2) || '0.00'}</strong>
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => handlePackagePaymentSelect('CASH')}
              sx={{
                py: 3,
                bgcolor: '#4CAF50',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#388E3C' },
              }}
            >
              💵 NAKİT
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={() => handlePackagePaymentSelect('POS')}
              sx={{
                py: 3,
                bgcolor: '#2196F3',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1976D2' },
              }}
            >
              💳 POS (Kredi Kartı)
            </Button>
            {otherPaymentOptions.length > 0 && (
              <>
                <Divider sx={{ my: 1 }}>Diğer Ödeme Yöntemleri</Divider>
                {otherPaymentOptions.map((option: any) => (
                  <Button
                    key={option.id}
                    variant="contained"
                    size="large"
                    onClick={() => handlePackagePaymentSelect(option.label?.toUpperCase() || option.id)}
                    sx={{
                      py: 2,
                      bgcolor: '#FF9800',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#F57C00' },
                    }}
                  >
                    🎫 {option.label || option.id}
                  </Button>
                ))}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setPackagePaymentModalOpen(false);
              setPendingPackageOrderData(null);
            }}
          >
            İptal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Satın Alınan Ürünler Dialog */}
      <Dialog
        open={isPurchaseItemsOpen}
        onClose={() => { setIsPurchaseItemsOpen(false); setSelectedRefundItem(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                Satın Alınan Ürünler
              </Typography>
              {purchaseItems.length > 0 && (
                <Chip label={`${purchaseItems.length} kalem`} size="small" color="primary" />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {selectedRefundItem && (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  disabled={refundLoading || selectedRefundItem?.isReceiptPrinted}
                  onClick={() => setRefundConfirmOpen(true)}
                  sx={{ fontWeight: 'bold' }}
                >
                  {refundLoading ? <CircularProgress size={18} /> : selectedRefundItem?.isReceiptPrinted ? 'FİŞ KESİLMİŞ - İADE YAPILAMAZ' : 'İADE AL'}
                </Button>
              )}
              {prepaidCard && (
                <>
                  <Chip label={prepaidCard.cardNumber || prepaidCard.cardUID || '-'} size="small" color="primary" variant="outlined" />
                  {prepaidCard.activeSession && (
                    <Chip label={`Oturum #${prepaidCard.activeSession.id}`} size="small" color="info" variant="outlined" />
                  )}
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1 }}>
          {purchaseItemsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : purchaseItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Bu oturumda henüz satın alınan ürün bulunmuyor.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell padding="checkbox" sx={{ width: 40 }}></TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ürün Adı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Satın Alma Zamanı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Miktar</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Birim Fiyat</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Toplam</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseItems.map((item: any, index: number) => {
                    const isReceipted = !!item.isReceiptPrinted;
                    // Her satın alma ayrı satır: seçim referans + ürün ile eşleşir
                    const isSelected = selectedRefundItem?.referenceId === item.referenceId
                      && selectedRefundItem?.productId === item.productId;
                    return (
                      <TableRow
                        key={`${item.referenceId || index}-${item.productId}`}
                        hover={!isReceipted}
                        selected={isSelected}
                        onClick={() => {
                          if (isSelected) setSelectedRefundItem(null);
                          else setSelectedRefundItem(item);
                        }}
                        sx={{
                          cursor: 'pointer',
                          '&.Mui-selected': { bgcolor: '#fff3e0' },
                          ...(isReceipted && { bgcolor: '#f5f5f5', opacity: 0.75 }),
                        }}
                      >
                        <TableCell padding="checkbox">
                          <input
                            type="radio"
                            checked={isSelected}
                            onChange={() => {
                              if (isSelected) setSelectedRefundItem(null);
                              else setSelectedRefundItem(item);
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        </TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{item.productName}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                          {item.purchasedAt
                            ? new Date(item.purchasedAt.endsWith?.('Z') ? item.purchasedAt : `${item.purchasedAt}Z`)
                                .toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {Number(item.totalQuantity).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {item.unit || 'Adet'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(item.totalAmount)}</TableCell>
                        <TableCell align="center">
                          {isReceipted ? (
                            <Chip label="Fişlendi" size="small" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                          ) : (
                            <Chip label="Fişlenmedi" size="small" color="default" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                    <TableCell colSpan={7} align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      GENEL TOPLAM
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                      {formatCurrency(purchaseItemsGrandTotal)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setIsPurchaseItemsOpen(false); setSelectedRefundItem(null); }} variant="outlined">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* İade Onay Dialog */}
      <Dialog
        open={refundConfirmOpen}
        onClose={() => setRefundConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>İade Onayı</DialogTitle>
        <DialogContent>
          {selectedRefundItem && (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Aşağıdaki ürünü iade etmek istediğinize emin misiniz?
              </Typography>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 1 }}>
                <Typography variant="body2"><strong>Ürün:</strong> {selectedRefundItem.productName}</Typography>
                {selectedRefundItem.purchasedAt && (
                  <Typography variant="body2"><strong>Satın Alma:</strong> {new Date(selectedRefundItem.purchasedAt.endsWith?.('Z') ? selectedRefundItem.purchasedAt : `${selectedRefundItem.purchasedAt}Z`).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                )}
                <Typography variant="body2"><strong>Miktar:</strong> {Number(selectedRefundItem.totalQuantity).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 3 })} {selectedRefundItem.unit || 'Adet'}</Typography>
                <Typography variant="body2"><strong>Tutar:</strong> {formatCurrency(selectedRefundItem.totalAmount)}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Bu tutar karta iade edilecek ve stok güncellenmesini sağlayacaktır.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundConfirmOpen(false)} variant="outlined" disabled={refundLoading}>
            İptal
          </Button>
          <Button onClick={handleRefundItem} variant="contained" color="warning" disabled={refundLoading}>
            {refundLoading ? <CircularProgress size={18} /> : 'İade Et'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ürün Kartı Sıralama - PIN Dialog */}
      <Dialog
        open={isProductSortPinDialogOpen}
        onClose={() => { setIsProductSortPinDialogOpen(false); setProductSortPinInput(''); setProductSortPinError(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', pb: 0 }}>{t('quickSaleScreen.productCardPinModal.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('quickSaleScreen.productCardPinModal.description')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            {[0, 1, 2, 3, 4].map(i => (
              <Box key={i} sx={{
                width: 40, height: 48, border: '2px solid', borderRadius: 1,
                borderColor: productSortPinError ? 'error.main' : (productSortPinInput.length > i ? 'primary.main' : 'divider'),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: productSortPinError ? 'error.lighter' : 'background.paper',
                transition: 'all 0.2s',
              }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: productSortPinError ? 'error.main' : 'text.primary' }}>
                  {productSortPinInput.length > i ? '●' : ''}
                </Typography>
              </Box>
            ))}
          </Box>
          {productSortPinError && (
            <Typography variant="caption" color="error" sx={{ mb: 1 }}>{t('quickSaleScreen.productCardPinModal.wrongPassword')}</Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, width: '100%', maxWidth: 240 }}>
            {['1','2','3','4','5','6','7','8','9'].map(d => (
              <Button key={d} variant="outlined" sx={{ fontSize: '1.3rem', py: 1.5 }} onClick={() => handleProductSortPinDigit(d)}>{d}</Button>
            ))}
            <Button variant="outlined" color="warning" sx={{ fontSize: '1rem', py: 1.5 }} onClick={handleProductSortPinBackspace}>⌫</Button>
            <Button variant="outlined" sx={{ fontSize: '1.3rem', py: 1.5 }} onClick={() => handleProductSortPinDigit('0')}>0</Button>
            <Button variant="outlined" disabled sx={{ py: 1.5 }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => { setIsProductSortPinDialogOpen(false); setProductSortPinInput(''); setProductSortPinError(false); }}>{t('quickSaleScreen.productCardPinModal.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default QuickSaleMiscDialogs;
